import React from 'react';
import {
  Box,
  Alert,
  Typography,
  Chip,
  Stack,
  useTheme
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { SEAT_UPGRADE_PREVIEW } from '@shop/graphql/queries/seatUpgradePreview';
import { stripePriceToEuro } from '@/utils/format';
import { mapGraphQLErrorToFriendlyMessage } from '@/utils/errorHandling';

interface SeatUpgradePreviewProps {
  currentSeats: { S: number; M: number; L: number };
  newSeats: { S: number; M: number; L: number };
  subscriptionId: string;
  isInTrial?: boolean;
  className?: string;
}

export const SeatUpgradePreview: React.FC<SeatUpgradePreviewProps> = ({
  currentSeats,
  newSeats,
  subscriptionId,
  isInTrial = false,
  className
}) => {
  const theme = useTheme();

  // Prüfe ob ein Upgrade stattfindet
  const hasUpgrade = newSeats.S > currentSeats.S || 
                     newSeats.M > currentSeats.M || 
                     newSeats.L > currentSeats.L;
  
  // GraphQL query für Backend-Kostenberechnung
  const { data: previewData, loading, error } = useQuery(SEAT_UPGRADE_PREVIEW, {
    variables: {
      subscriptionId,
      seatsS: newSeats.S,
      seatsM: newSeats.M,
      seatsL: newSeats.L,
    },
    skip: !hasUpgrade || !subscriptionId,
  });

  if (!hasUpgrade) {
    return null; // Keine Anzeige wenn kein Upgrade
  }

  if (loading) {
    return (
      <Alert severity="info" className={className}>
        Berechne Upgrade-Kosten...
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className={className}>
        Fehler bei der Kostenberechnung: {mapGraphQLErrorToFriendlyMessage(error)}
      </Alert>
    );
  }

  const preview = previewData?.seatUpgradePreview;
  const totalCost = preview?.grossTotal || 0;
  const taxAmount = preview?.taxAmount || 0;
  const hasBreakdown = preview?.breakdown && preview.breakdown.length > 0;

  // Berechne hinzugefügte Sitze für Anzeige
  const addedSeats = {
    S: Math.max(0, newSeats.S - currentSeats.S),
    M: Math.max(0, newSeats.M - currentSeats.M),
    L: Math.max(0, newSeats.L - currentSeats.L),
  };

  const seatAdditions = Object.entries(addedSeats)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => `+${count} ${type}`)
    .join(', ');

  const seatColors = {
    S: theme.palette.semantic.planSize.S,
    M: theme.palette.semantic.planSize.M,
    L: theme.palette.semantic.planSize.L
  };

  return (
    <Alert 
      severity={isInTrial ? "info" : "warning"} 
      className={className}
      sx={{ 
        '& .MuiAlert-message': { width: '100%' }
      }}
    >
      <Stack spacing={1}>
        {/* Header mit Seat-Änderungen */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Sitz-Upgrade: {seatAdditions}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {stripePriceToEuro(totalCost)}
          </Typography>
        </Box>

        {/* Trial-spezifische Nachricht */}
        {isInTrial ? (
          <Typography variant="body2" color="info.dark">
            ✨ <strong>Kostenlos während der Testphase!</strong> 
            {hasBreakdown && (
              <> Die vollen Kosten ({stripePriceToEuro(totalCost)}) werden mit der ersten Jahresrechnung fällig.</>
            )}
          </Typography>
        ) : (
          // Aktives Abonnement - zeige Kostenaufschlüsselung
          hasBreakdown && (
            <Typography variant="body2" color="text.secondary">
              Sofort fällig für die verbleibenden Monate des Jahresabonnements{taxAmount>0 && ` (inkl. ${stripePriceToEuro(taxAmount)} MwSt.)`}
            </Typography>
          )
        )}
      </Stack>
    </Alert>
  );
}; 